import { PageHeader } from "@/components/PageHeader";
import { SortableHeader } from "@/components/SortableHeader";
import { api, type PaginatedResponse } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import type { Organization } from "@/types";
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@a7y/tailwind-catalyst";
import { useQuery } from "@tanstack/react-query";
import { Building2, ChevronRight, Search, X } from "lucide-react";
import { type ChangeEvent, useRef, useState } from "react";

type SortField = "name" | "planId" | "billingStatus" | "memberCount" | "createdAt";
type SortOrder = "asc" | "desc";

const PLAN_COLORS: Record<string, string> = {
  free: "zinc",
  basic: "zinc",
  pro: "purple",
  elite: "blue",
  enterprise: "orange",
};

const STATUS_COLORS: Record<string, string> = {
  active: "green",
  trialing: "blue",
  past_due: "amber",
  grace_overage: "amber",
  canceled: "red",
  sandbox: "zinc",
};

export function OrganizationsPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [includeApptonomy, setIncludeApptonomy] = useState(false);

  // Debounce search: only query when user stops typing
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 400);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder(field === "createdAt" ? "desc" : "asc");
    }
    setPage(1);
  };

  const { data, isLoading } = useQuery<PaginatedResponse<Organization>>({
    queryKey: ["organizations", { page, search: debouncedSearch, sortField, sortOrder, includeApptonomy }],
    queryFn: () =>
      api.getOrganizations({
        page,
        limit: 20,
        search: debouncedSearch || undefined,
        sortBy: sortField,
        sortOrder,
        includeApptonomy,
      }),
  });

  // Search spans every organization, so the @apptonomy.ai toggle only governs the unsearched list.
  const searchActive = search.trim() !== "";

  const organizations = data?.data ?? [];
  const total = data?.total ?? 0;
  const pageSize = 20;

  return (
    <div className="px-4 py-0">
      <PageHeader title="Organizations" />

      <div className="mb-6 flex items-center gap-3">
        <div className="relative w-[340px]">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            autoComplete="off"
            placeholder="Search by name, slug or org ID..."
            value={search}
            onChange={handleSearchChange}
            className="w-full rounded-lg border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 pl-10 pr-10 py-2 text-gray-900 dark:text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {search && (
            <button
              onClick={() => { setSearch(""); setDebouncedSearch(""); setPage(1); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <label
          className={`flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-300${searchActive ? " opacity-50" : ""}`}
          title={searchActive ? "Search covers all organizations, including @apptonomy.ai" : undefined}
        >
          <input
            type="checkbox"
            checked={includeApptonomy}
            disabled={searchActive}
            onChange={(e) => {
              setIncludeApptonomy(e.target.checked);
              setPage(1);
            }}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-zinc-600 dark:bg-zinc-800"
          />
          Include @apptonomy.ai
        </label>
      </div>

      {/* Organizations Table */}
      <Table dense striped>
        <TableHead>
          <TableRow>
            <SortableHeader field="name" label="Organization" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
            <SortableHeader field="planId" label="Plan" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
            <SortableHeader field="billingStatus" label="Status" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
            <SortableHeader field="memberCount" label="Members" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
            <SortableHeader field="createdAt" label="Created" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
            <TableHeader className="text-right">Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center">
                <div className="h-6 w-6 animate-spin rounded-full border-4 border-gray-300 dark:border-zinc-600 border-t-blue-600 mx-auto" />
              </TableCell>
            </TableRow>
          ) : organizations.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8">
                <Building2 className="mx-auto h-12 w-12 text-zinc-400" />
                <p className="mt-2 text-zinc-500">
                  {debouncedSearch
                    ? "No organizations found matching your search"
                    : "No organizations found"}
                </p>
              </TableCell>
            </TableRow>
          ) : (
            organizations.map((org) => (
              <TableRow key={org.id} href={`/admin/organizations/${org.id}`}>
                <TableCell className="font-medium">
                  <div>
                    {org.name}
                    <div className="text-xs font-mono text-zinc-500">{org.id}</div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <Badge color={PLAN_COLORS[org.planId] ?? "zinc"}>
                      {org.planId.toUpperCase()}
                    </Badge>
                    {org.overridePlanId && (
                      <Badge color="amber">OVERRIDE</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    color={STATUS_COLORS[org.billingStatus ?? ""] ?? "zinc"}
                  >
                    {(org.billingStatus ?? "unknown").toUpperCase().replace("_", " ")}
                  </Badge>
                </TableCell>
                <TableCell>{org.memberCount}</TableCell>
                <TableCell className="text-zinc-500">{formatDate(org.createdAt)}</TableCell>
                <TableCell className="text-right">
                  <ChevronRight className="inline h-5 w-5 text-zinc-400" />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      {total > pageSize && (
        <div className="mt-6 flex items-center justify-between">
          <Text className="text-sm text-gray-700 dark:text-zinc-300">
            Showing <span className="font-medium">{(page - 1) * pageSize + 1}</span> to{" "}
            <span className="font-medium">{Math.min(page * pageSize, total)}</span> of{" "}
            <span className="font-medium">{total}</span> results
          </Text>
          <div className="flex gap-2">
            <Button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              outline
            >
              Previous
            </Button>
            <Button
              onClick={() => setPage(page + 1)}
              disabled={page * pageSize >= total}
              outline
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
